import { useEffect, useState, type ReactNode } from "react";
import { getJson } from "../api";
import { formatText, useTexts, type Label, type Locale } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState } from "../ui/pieces";
import { money, plural, routeName } from "./ai";
import { useTitle } from "./parts";
import {
  Counter,
  DataTable,
  FilterRow,
  Indicator,
  IndicatorSkeleton,
  Indicators,
  MoreFilters,
  NoResults,
  PeriodPill,
  Pill,
  PillDivider,
  RowsSkeleton,
  useFilters,
} from "./widgets";
import styles from "./ai.module.css";

export type Totals = {
  executions: number;
  errors: number;
  inputTokens: number;
  outputTokens: number;
  cost: number | null;
  currency: string | null;
  mixedCurrencies: boolean;
  unpricedExecutions: number;
};

type Group = {
  organizationId: number | null;
  providerModelId: number | null;
  modelName: string | null;
  providerName: string | null;
  routeCode: string | null;
  organizationName: string | null;
  /** AR9 · el código distingue dos empresas con el mismo nombre en la página. */
  organizationCode?: string | null;
  totals: Totals;
};

/** Los filtros que comparten Resumen y Ejecuciones (todo en la dirección, E84). */
export const usageKeys = ["period", "mode", "type", "organizationId"] as const;

/** Lo compartido de la dirección actual («period=7d&mode=simulation…»), sin página ni desglose. */
export function sharedQuery(params: URLSearchParams): string {
  const shared = new URLSearchParams();
  for (const key of usageKeys) {
    const value = params.get(key);
    if (value) shared.set(key, value);
  }
  return shared.toString();
}

/** Pestañas Resumen · Ejecuciones (C03), con el filtro compartido. */
export function UsageTabs({ current }: { current: "summary" | "executions" }) {
  const { L } = useTexts();
  const { params } = useFilters();
  const shared = sharedQuery(params);
  const suffix = shared ? `?${shared}` : "";
  return (
    <nav className={`tm-tabs ${styles.tabs}`} aria-label={L("ADM_AI_USAGE")} data-part="pestanas">
      <a href={`/Ai/Uso/Index${suffix}`} aria-current={current === "summary" ? "page" : undefined}>
        {L("ADM_AI_SUMMARY")}
      </a>
      <a href={`/Ai/Uso/Ejecuciones${suffix}`} aria-current={current === "executions" ? "page" : undefined}>
        {L("ADM_AI_EXECUTIONS")}
      </a>
    </nav>
  );
}

const types = ["conversation", "connection-test", "embedding", "benchmark"] as const;
const typeKey: Record<string, string> = {
  conversation: "ADM_AI_TYPE_CONVERSATION",
  "connection-test": "ADM_AI_TYPE_TEST",
  embedding: "ADM_AI_TYPE_EMBEDDING",
  benchmark: "ADM_AI_TYPE_BENCHMARK",
};

/**
 * Fila de filtros de Uso y costos (C07): período, Reales · Simulación y «Más filtros» con Tipo y Empresa (con buscador,
 * las empresas crecen). Lo aplicado se ve como píldora encendida; tocarla la quita. Dentro de una empresa (Uso y soporte
 * › Uso de IA, R4) la empresa ya está fija: `withOrganization={false}` deja solo el Tipo.
 */
export function UsageFilters({ withOrganization = true }: { withOrganization?: boolean } = {}) {
  const { L } = useTexts();
  const { get, set } = useFilters();
  const period = get("period") || "30d";
  const mode = get("mode");
  const type = get("type");
  const organizationId = get("organizationId");
  const [orgSearch, setOrgSearch] = useState("");
  const [orgs, setOrgs] = useState<{ organizationId: number; name: string }[]>([]);
  const [orgName, setOrgName] = useState<string | null>(null);
  useEffect(() => {
    if (!withOrganization) return;
    let live = true;
    const query = new URLSearchParams({ pageSize: "5", ...(orgSearch ? { search: orgSearch } : {}) });
    void getJson<{ items: { organizationId: number; name: string }[] }>(`/api/admin/organizations/page?${query}`).then(
      (reply) => live && reply.status === 200 && reply.body && setOrgs(reply.body.items),
    );
    return () => {
      live = false;
    };
  }, [orgSearch, withOrganization]);
  // El nombre de la empresa aplicada (la píldora lo dice), leído de su ficha solo si no está entre las opciones.
  const known = orgs.find((org) => String(org.organizationId) === organizationId)?.name ?? null;
  useEffect(() => {
    if (!withOrganization || !organizationId || known) return;
    let live = true;
    void getJson<{ organizationId: number; name: string }[]>(
      `/api/admin/ai/organization-names?ids=${encodeURIComponent(organizationId)}`,
    ).then((reply) => {
      if (live) setOrgName(reply.status === 200 ? (reply.body?.[0]?.name ?? null) : null);
    });
    return () => {
      live = false;
    };
  }, [organizationId, known, withOrganization]);
  const appliedOrg = withOrganization && organizationId ? (known ?? orgName ?? L("ADM_COMPANY")) : null;
  const orgOptions = orgs.map((org) => ({ value: String(org.organizationId), label: org.name }));
  if (organizationId && !orgOptions.some((option) => option.value === organizationId))
    orgOptions.unshift({ value: organizationId, label: appliedOrg ?? L("ADM_COMPANY") });
  return (
    <FilterRow label={L("ADM_AI_FILTERS")}>
      <PeriodPill value={period} onChange={(value) => set({ period: value === "30d" ? "" : value })} />
      <Pill pressed={mode !== "simulation"} onClick={() => set({ mode: "" })}>
        {L("ADM_AI_MODE_REAL")}
      </Pill>
      <Pill pressed={mode === "simulation"} onClick={() => set({ mode: "simulation" })}>
        {L("ADM_AI_MODE_SIMULATION")}
      </Pill>
      {type ? (
        <Pill
          pressed
          onClick={() => set({ type: "" })}
          label={`${L("ADM_AI_REMOVE_FILTER")}: ${L(typeKey[type] ?? "")}`}
        >
          {L(typeKey[type] ?? "USAGE_UNKNOWN")}
          <ChatIcon name="x" />
        </Pill>
      ) : null}
      {appliedOrg ? (
        <Pill pressed onClick={() => set({ organizationId: "" })} label={`${L("ADM_AI_REMOVE_FILTER")}: ${appliedOrg}`}>
          {appliedOrg}
          <ChatIcon name="x" />
        </Pill>
      ) : null}
      <PillDivider />
      <MoreFilters
        groups={[
          {
            key: "type",
            title: L("ADM_AI_TYPE"),
            // La comparativa de modelos es de la plataforma, no de una empresa (§5.8: Tipo sin «Comparativa»).
            options: types
              .filter((value) => withOrganization || value !== "benchmark")
              .map((value) => ({ value, label: L(typeKey[value]!) })),
          },
          ...(withOrganization
            ? [
                {
                  key: "organizationId",
                  title: L("ADM_COMPANY"),
                  options: orgOptions,
                  search: {
                    id: "usage-org-search",
                    placeholder: L("ADM_ORG_SEARCH"),
                    value: orgSearch,
                    onChange: setOrgSearch,
                  },
                },
              ]
            : []),
        ]}
        value={withOrganization ? { type, organizationId } : { type }}
        onApply={(next) =>
          set(
            withOrganization
              ? { type: next.type ?? "", organizationId: next.organizationId ?? "" }
              : { type: next.type ?? "" },
          )
        }
      />
    </FilterRow>
  );
}

/** C32 · comparación con el período anterior: en % desde 20 y entre −200 % y +200 %; si no, «vs. {n}». */
export function comparison(L: Label, locale: Locale, current: number, previous: number): string {
  const change = previous > 0 ? ((current - previous) / previous) * 100 : null;
  if (previous >= 20 && change !== null && Math.abs(change) <= 200) {
    const sign = change > 0 ? "+" : change < 0 ? "−" : "";
    const value = Math.abs(change).toLocaleString(locale, {
      maximumFractionDigits: Math.abs(change) >= 100 ? 0 : 1,
    });
    return formatText(L("ADM_AI_VS_PREVIOUS_PCT"), `${sign}${value}`);
  }
  return formatText(L("ADM_AI_VS_PREVIOUS"), previous.toLocaleString(locale));
}

/** Costo de un total: «—» sin precio o con varias monedas (nunca 0 inventado). */
const totalCost = (locale: Locale, totals: Totals) => money(locale, totals.cost, totals.currency);

/**
 * IA › Uso y costos › Resumen (§5.9 `ia-uso`, D14): cinco indicadores sumados en el servidor (B1) con el período
 * anterior, y el desglose Por empresa · Por modelo · Por ruta paginado. La fila «Plataforma (pruebas de conexión)»
 * agrupa lo que no es de una empresa. Una empresa lleva a su Uso de IA con el mismo período (E84).
 */
export function UsagePage() {
  const { L, locale } = useTexts();
  const { get, set, params } = useFilters();
  useTitle(L("ADM_AI_USAGE"));
  const by = get("by") === "modelo" ? "modelo" : get("by") === "ruta" ? "ruta" : "empresa";
  const shared = sharedQuery(params);
  const list = useServerList<Group>(`/api/admin/ai/usage/summary?${shared}${shared ? "&" : ""}by=${by}`);
  const totals = (list.extra?.totals as Totals | undefined) ?? null;
  const previous = (list.extra?.previous as Totals | undefined) ?? null;
  const head = (
    <>
      <PageHead title={L("ADM_AI_USAGE")} sub={L("ADM_AI_USAGE_SUB")} />
      <UsageTabs current="summary" />
    </>
  );
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  if (list.state === "error" || list.state === "notFound")
    return (
      <>
        {head}
        <UsageFilters />
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      </>
    );

  const percent = (part: number, whole: number) =>
    whole === 0 ? "0" : ((part / whole) * 100).toLocaleString(locale, { maximumFractionDigits: 1 });
  const costLine = (value: Totals): ReactNode =>
    value.mixedCurrencies
      ? L("ADM_AI_COST_MIXED")
      : value.unpricedExecutions > 0
        ? plural(L, locale, value.unpricedExecutions, "ADM_AI_UNPRICED_ONE", "ADM_AI_UNPRICED")
        : L("ADM_AI_COST_LINE");
  const groupName = (group: Group) =>
    by === "empresa"
      ? group.organizationId == null
        ? L("ADM_AI_PLATFORM_TESTS")
        : (group.organizationName ?? L("ADM_AI_ORG_OUT_OF_SCOPE"))
      : by === "modelo"
        ? group.providerModelId == null
          ? L("ADM_AI_NO_MODEL_GROUP")
          : [group.modelName, group.providerName].filter(Boolean).join(" · ") || L("USAGE_UNKNOWN")
        : group.routeCode == null
          ? L("ADM_AI_NO_ROUTE_GROUP")
          : routeName(L, group.routeCode);
  const href = (group: Group) =>
    by === "empresa" && group.organizationId != null && group.organizationName
      ? `/Organizations/Uso?id=${group.organizationId}${
          shared
            ? `&${new URLSearchParams(
                [...new URLSearchParams(shared)].filter(([key]) => key !== "organizationId"),
              ).toString()}`
            : ""
        }`.replace(/&$/, "")
      : null;
  // AR8 · columna con cantidades completas («16,104 / 3,031»), nunca «16.1 k» al lado de «3,339».
  const tokens = (value: Totals) =>
    `${value.inputTokens.toLocaleString(locale)} / ${value.outputTokens.toLocaleString(locale)}`;
  const counterKeys =
    by === "empresa"
      ? ["ADM_AI_N_ORGS_ONE", "ADM_AI_N_ORGS"]
      : by === "modelo"
        ? ["ADM_N_MODELS_ONE", "ADM_N_MODELS"]
        : ["ADM_AI_N_ROUTES_ONE", "ADM_AI_N_ROUTES"];
  const filtered = !!(get("type") || get("organizationId"));
  // AR9 · nombres repetidos en la página: la segunda línea lleva el código de la empresa.
  const repeated = (group: Group) =>
    by === "empresa" &&
    !!group.organizationCode &&
    list.items.filter((other) => other.organizationName === group.organizationName).length > 1;

  return (
    <>
      {head}
      <div className={styles.toolbar}>
        <UsageFilters />
      </div>
      {totals && previous ? (
        <Indicators label={L("ADM_AI_USAGE")} count={5}>
          <Indicator
            label={L("ADM_AI_EXECUTIONS")}
            value={totals.executions.toLocaleString(locale)}
            line={comparison(L, locale, totals.executions, previous.executions)}
          />
          <Indicator
            label={L("ADM_AI_WITH_ERROR")}
            value={totals.errors.toLocaleString(locale)}
            line={
              totals.executions === 0
                ? L("ADM_AI_NO_EXECUTIONS_PERIOD")
                : formatText(L("ADM_AI_ERROR_PCT"), percent(totals.errors, totals.executions))
            }
          />
          <Indicator
            label={L("ADM_AI_INPUT_TOKENS")}
            value={totals.inputTokens.toLocaleString(locale)}
            small
            line={L("ADM_AI_INPUT_TOKENS_LINE")}
          />
          <Indicator
            label={L("ADM_AI_OUTPUT_TOKENS")}
            value={totals.outputTokens.toLocaleString(locale)}
            small
            line={L("ADM_AI_OUTPUT_TOKENS_LINE")}
          />
          <Indicator
            label={L("ADM_AI_COST")}
            value={totalCost(locale, totals)}
            small
            muted={totals.cost == null}
            href={totals.unpricedExecutions > 0 ? "/Ai/Modelos/Index" : undefined}
            line={costLine(totals)}
          />
        </Indicators>
      ) : (
        <IndicatorSkeleton count={5} label={L("ADM_AI_USAGE")} />
      )}
      <FilterRow label={L("ADM_AI_BREAKDOWN")}>
        {(["empresa", "modelo", "ruta"] as const).map((value) => (
          <Pill key={value} pressed={by === value} onClick={() => set({ by: value === "empresa" ? "" : value })}>
            {L(value === "empresa" ? "ADM_AI_BY_ORG" : value === "modelo" ? "ADM_AI_BY_MODEL" : "ADM_AI_BY_ROUTE")}
          </Pill>
        ))}
      </FilterRow>
      {list.state === "loading" ? (
        <RowsSkeleton rows={4} table />
      ) : list.items.length === 0 ? (
        filtered ? (
          <NoResults query="" things="" onClear={() => set({ type: "", organizationId: "" })} />
        ) : (
          <div className="tm-empty" role="status">
            <strong>{L("ADM_AI_NO_USAGE")}</strong>
            <span>{L("ADM_AI_NO_USAGE_LINE")}</span>
            <button type="button" className="tm-btn secondary" onClick={() => set({ period: "90d" })}>
              {L("ADM_AI_SEE_90D")}
            </button>
          </div>
        )
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, counterKeys[0]!, counterKeys[1]!)}</Counter>
          <DataTable<Group>
            label={L("ADM_AI_BREAKDOWN")}
            rows={list.items}
            rowKey={(group) => `${group.organizationId ?? group.providerModelId ?? group.routeCode ?? "none"}`}
            href={href}
            columns={[
              {
                key: "name",
                title: L(by === "empresa" ? "ADM_COMPANY" : by === "modelo" ? "ADM_AI_COL_MODEL" : "ADM_AI_COL_ROUTE"),
                render: (group) =>
                  repeated(group) ? (
                    <span className={styles.cellMain}>
                      <strong>{groupName(group)}</strong>
                      <span className={styles.cellSub}>{group.organizationCode}</span>
                    </span>
                  ) : (
                    <strong>{groupName(group)}</strong>
                  ),
              },
              {
                key: "executions",
                title: L("ADM_AI_EXECUTIONS"),
                numeric: true,
                render: (group) => group.totals.executions.toLocaleString(locale),
              },
              {
                key: "errors",
                title: L("ADM_AI_WITH_ERROR"),
                numeric: true,
                render: (group) => group.totals.errors.toLocaleString(locale),
              },
              {
                key: "tokens",
                title: L("ADM_AI_COL_TOKENS_IO"),
                numeric: true,
                render: (group) => tokens(group.totals),
              },
              {
                key: "cost",
                title: L("ADM_AI_COST"),
                numeric: true,
                render: (group) => totalCost(locale, group.totals),
              },
            ]}
            mobile={(group) => ({
              title: groupName(group),
              line: [
                repeated(group) ? group.organizationCode : null,
                group.totals.executions.toLocaleString(locale),
                group.totals.errors.toLocaleString(locale),
                tokens(group.totals),
                totalCost(locale, group.totals),
              ]
                .filter(Boolean)
                .join(" · "),
            })}
          />
          <ListFooter list={list} />
          <p className={styles.note}>
            {list.total > list.items.length ? L("ADM_AI_TABLE_PAGES_NOTE") : L("ADM_AI_TABLE_SUMS_NOTE")}
          </p>
        </>
      )}
    </>
  );
}
